import { RoutingImportCohort, Route, Stop } from 'src/graphql/types/delivery';
import { GRoutingPlanCohort } from 'src/graphql/types/generated';
import { getRoutingCohorts, isPooledCohortlessStop } from 'src/utils';

// Multi-fleet lets a market turn a cohort "off": its stops pool into the shared fleet instead of
// routing on its own trucks (mirrors CohortTruckCounts.tsx / cohortTruckCounts.ts). The deliveries
// page derives the pooled bucket client-side from the same stop→cohort attribution the Edit card
// already uses, so the backend only needs to expose each cohort's `enabled` flag.

export type PooledAggregate = {
  // Off cohort ids, so the Edit card can persist the pooled truck count back through the existing
  // per-cohort updateTruckCount mutation.
  cohortIds: string[];
  locationCount: number;
  totalWeight: number;
  totalVolume: number;
  // Sum of the off cohorts' own truck counts — the trucks that make up the shared pool.
  truckCount: number;
};

export const isCohortOff = (cohort: RoutingImportCohort): boolean =>
  cohort?.enabled === false;

// A dormant cohort has no member locations and a null or 0 count: routing ignores it.
type DormantFlag = Pick<GRoutingPlanCohort, 'dormant'>;

export const isCohortDormant = (cohort: DormantFlag): boolean =>
  cohort.dormant === true;

// The cohorts routing acts on: with pooled stops on, dormant cohorts are left out, so a plan whose
// cohorts are all dormant (SMF1) reads as a plan with no cohorts. With it off, every cohort, as before.
export const activeCohorts = <T extends DormantFlag>(
  cohorts: T[],
  pooledStopsEnabled: boolean,
): T[] =>
  pooledStopsEnabled
    ? cohorts.filter((cohort) => !isCohortDormant(cohort))
    : cohorts;

// The cohorts a location cohort picker offers. At a pool warehouse (every cohort dormant, with pooled stops
// on) none: assigning a location to an empty cohort would make it a cohort warehouse and its pool would
// stop counting. At a cohort warehouse every cohort, empty ones included, since the picker is how a new
// cohort gets its first locations.
export const pickerCohorts = <T extends DormantFlag>(
  cohorts: T[],
  pooledStopsEnabled: boolean,
): T[] =>
  pooledStopsEnabled && cohorts.length > 0 && cohorts.every(isCohortDormant)
    ? []
    : cohorts;

// A pool warehouse routes from its whole-day pool: it has no cohorts routing acts on. Pooled stops
// only; callers gate on the flag.
export const isPoolWarehouse = (cohorts: RoutingImportCohort[]): boolean =>
  activeCohorts(cohorts, true).length === 0;

export const splitCohortsByEnabled = (
  cohorts: RoutingImportCohort[],
): { onCohorts: RoutingImportCohort[]; offCohorts: RoutingImportCohort[] } => {
  const onCohorts: RoutingImportCohort[] = [];
  const offCohorts: RoutingImportCohort[] = [];
  cohorts.forEach((cohort) => {
    if (isCohortOff(cohort)) offCohorts.push(cohort);
    else onCohorts.push(cohort);
  });
  return { onCohorts, offCohorts };
};

// Aggregate the off cohorts' stops into a single pooled bucket. Weight/volume mirror the per-cohort
// computation in EditRouteImportCard (Math.ceil over Delivery stops). With includeCohortless, opted-in
// stops without a cohort join the bucket; a stop has a cohort or none, so none is counted twice.
export const computePooledAggregate = (
  offCohorts: RoutingImportCohort[],
  routes: Route[],
  { includeCohortless }: { includeCohortless: boolean },
): PooledAggregate => {
  const routeCohorts = getRoutingCohorts(routes, { includePooled: false });
  const offIds = new Set(offCohorts.map((cohort) => cohort.id));

  let locationCount = 0;
  let totalWeight = 0;
  let totalVolume = 0;
  const addStop = (stop: Stop): void => {
    if (stop.__typename !== 'Delivery') return;
    locationCount += 1;
    totalWeight += stop.totalWeight ?? 0;
    totalVolume += stop.totalVolume ?? 0;
  };
  routeCohorts.forEach((routeCohort) => {
    if (!offIds.has(routeCohort.id)) return;
    routeCohort.stops.forEach(addStop);
  });
  if (includeCohortless) {
    routes.forEach((route) =>
      route.stops?.filter(isPooledCohortlessStop).forEach(addStop),
    );
  }

  const truckCount = offCohorts.reduce(
    (sum, cohort) => sum + (cohort.truckCount ?? 0),
    0,
  );

  return {
    cohortIds: offCohorts.map((cohort) => cohort.id),
    locationCount,
    totalWeight: Math.ceil(totalWeight),
    totalVolume: Math.ceil(totalVolume),
    truckCount,
  };
};
